import React, { useState } from 'react';
import { Box, ListSubheader, Menu, MenuItem } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import MicIcon from '@mui/icons-material/Mic';
import VideocamIcon from '@mui/icons-material/Videocam';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import { useTranslation } from 'react-i18next';
import { useLocalParticipant, useRoomContext } from '@livekit/components-react';
import { TrackPublication, supportsAudioOutputSelection } from 'livekit-client';

import { addWarningBreadcrumb } from '../../../lib/sentry';
import { reportDeviceSwitchError } from '../callSignals';
import {
  deviceOptions,
  resolveActiveDeviceId,
  trackDeviceId,
} from '../deviceOptions';
import { DS } from '../designTokens';
import { useLocalDevices } from '../hooks/useLocalDevices';
import { DeviceSwitch, LocalDevices } from '../types';

const MenuNote = ({
  warning = false,
  children,
}: {
  warning?: boolean;
  children: React.ReactNode;
}) => (
  <Box
    role={warning ? 'alert' : 'status'}
    sx={{
      px: 1.5,
      py: 1.25,
      borderRadius: '12px',
      bgcolor: warning ? DS.dawnSurface : 'transparent',
      fontFamily: DS.fontBody,
      fontSize: 13,
      lineHeight: 1.45,
      color: warning ? DS.softBlack : DS.muted,
    }}
  >
    {children}
  </Box>
);

const retryItemSx = {
  minHeight: 44,
  px: 1.5,
  borderRadius: '12px',
  fontFamily: DS.fontBody,
  fontSize: 14,
  fontWeight: 700,
  color: DS.deepTeal,
  '&:hover': { bgcolor: DS.linen },
};

const headingSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  lineHeight: 1.4,
  pt: 1,
  pb: 1,
  px: 1.5,
  fontFamily: DS.fontBody,
  fontWeight: 900,
  fontSize: 12,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: DS.muted,
  '& svg': { fontSize: 18 },
};

const DeviceListNote = ({
  devices,
  onRetry,
}: {
  devices: LocalDevices;
  onRetry: () => void;
}) => {
  const { t } = useTranslation();
  if (devices.kind === 'error') {
    return (
      <>
        <MenuNote warning>{t('videoVisit.devices.loadFailed')}</MenuNote>
        <MenuItem onClick={onRetry} sx={retryItemSx}>
          {t('videoVisit.retry')}
        </MenuItem>
      </>
    );
  }
  if (devices.kind !== 'ready') {
    return <MenuNote>{t('videoVisit.devices.loading')}</MenuNote>;
  }
  const found = Object.values(devices.devices).some(
    (list) => deviceOptions(list).length > 0
  );
  if (found) return null;
  return <MenuNote>{t('videoVisit.devices.none')}</MenuNote>;
};

const DeviceSection = ({
  icon,
  title,
  unnamedKey,
  devices,
  activeId,
  onSelect,
}: {
  icon: React.ReactNode;
  title: string;
  unnamedKey: string;
  devices: MediaDeviceInfo[];
  activeId?: string;
  onSelect: (deviceId: string, name: string) => void;
}) => {
  const { t } = useTranslation();
  const options = deviceOptions(devices);
  if (options.length === 0) return null;
  // No wrapper element: MUI moves focus between the menu's direct DOM
  // children, so the rows have to sit directly in the list.
  return (
    <>
      <ListSubheader sx={headingSx}>
        {icon}
        {title}
      </ListSubheader>
      {options.map((option, index) => {
        const name = option.label || t(unnamedKey, { number: index + 1 });
        const active = option.deviceId === activeId;
        return (
          <MenuItem
            key={option.deviceId}
            role="menuitemradio"
            aria-checked={active}
            onClick={() => onSelect(option.deviceId, name)}
            sx={{
              minHeight: 44,
              gap: 1,
              px: 1.5,
              whiteSpace: 'normal',
              borderRadius: '12px',
              fontFamily: DS.fontBody,
              fontSize: 14,
              fontWeight: active ? 700 : 400,
              color: DS.softBlack,
              '&:hover': { bgcolor: DS.linen },
            }}
          >
            <CheckIcon
              aria-hidden
              sx={{
                fontSize: 18,
                color: DS.deepTeal,
                visibility: active ? 'visible' : 'hidden',
              }}
            />
            {name}
          </MenuItem>
        );
      })}
    </>
  );
};

export type Opens = 'down' | 'up';

const menuPosition = (opens: Opens) => {
  if (opens === 'down') {
    return {
      anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
      transformOrigin: { vertical: 'top', horizontal: 'right' },
      offset: { mt: 1 },
    } as const;
  }
  return {
    anchorOrigin: { vertical: 'top', horizontal: 'center' },
    transformOrigin: { vertical: 'bottom', horizontal: 'center' },
    offset: { mt: -1 },
  } as const;
};

type Props = {
  anchor: HTMLElement | null;
  opens: Opens;
  onClose: () => void;
};

const DeviceMenu = ({ anchor, opens, onClose }: Props) => {
  const { t } = useTranslation();
  const room = useRoomContext();
  const { cameraTrack, microphoneTrack } = useLocalParticipant();
  const { state: devices, refresh } = useLocalDevices();
  const [switchState, setSwitchState] = useState<DeviceSwitch>({
    kind: 'idle',
  });

  const onOpen = () => {
    setSwitchState({ kind: 'idle' });
    refresh();
  };
  // LiveKit stops the current camera or microphone before it opens the new one
  // and does not reopen it when that fails, so the menu switches back itself.
  const switchBack = (
    kind: MediaDeviceKind,
    previousId?: string
  ): Promise<void> => {
    if (!previousId) return Promise.resolve();
    return room.switchActiveDevice(kind, previousId, true).then((switched) => {
      if (!switched) {
        addWarningBreadcrumb('video', `LiveKit could not switch ${kind} back`);
      }
    }, reportDeviceSwitchError);
  };

  // The menu stays open after a switch, so the clinician can change the camera,
  // microphone and speaker in one go and see a failure next to their pick.
  const switchTo =
    (kind: MediaDeviceKind) => (deviceId: string, name: string) => {
      // Rows stay enabled while a switch runs, because disabling the focused
      // row would drop keyboard focus out of the menu.
      if (switchState.kind === 'switching') return;
      setSwitchState({ kind: 'switching' });
      const previousId = room.getActiveDevice(kind);
      const fail = () =>
        switchBack(kind, previousId).then(() =>
          setSwitchState({ kind: 'failed', name })
        );
      room
        .switchActiveDevice(kind, deviceId, true)
        .then((switched) => {
          if (switched) {
            setSwitchState({ kind: 'idle' });
            return undefined;
          }
          addWarningBreadcrumb('video', `LiveKit could not switch ${kind}`);
          return fail();
        })
        .catch((err: unknown) => {
          reportDeviceSwitchError(err);
          return fail();
        });
    };

  const listOf = (kind: MediaDeviceKind): MediaDeviceInfo[] =>
    devices.kind === 'ready' ? devices.devices[kind] : [];

  const activeIdOf = (kind: MediaDeviceKind, publication?: TrackPublication) =>
    resolveActiveDeviceId({
      devices: listOf(kind),
      activeDeviceId: room.getActiveDevice(kind) ?? 'default',
      trackDeviceId: trackDeviceId(publication),
    });

  const position = menuPosition(opens);

  return (
    <Menu
      anchorEl={anchor}
      open={Boolean(anchor)}
      onClose={onClose}
      TransitionProps={{ onEnter: onOpen }}
      // The list takes focus, and arrow keys then move through the rows.
      // Focusing a row needs MUI to see the rows as the menu's own children.
      disableAutoFocusItem
      anchorOrigin={position.anchorOrigin}
      transformOrigin={position.transformOrigin}
      PaperProps={{
        sx: {
          ...position.offset,
          // Fixed, so the menu does not jump when a picked name turns bold.
          width: 340,
          borderRadius: '16px',
          boxShadow:
            '0 16px 40px rgba(36,40,41,0.10), 0 4px 12px rgba(36,40,41,0.06)',
        },
      }}
      MenuListProps={{
        sx: {
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          // A section without devices renders nothing, so the line goes above
          // every heading that has another heading before it.
          '& .MuiListSubheader-root ~ .MuiListSubheader-root': {
            mt: 1,
            pt: 2,
            borderTop: `1px solid ${DS.lineSoft}`,
          },
        },
      }}
    >
      {switchState.kind === 'failed' && (
        <MenuNote warning>
          {t('videoVisit.devices.switchFailed', { name: switchState.name })}
        </MenuNote>
      )}
      <DeviceListNote devices={devices} onRetry={refresh} />
      <DeviceSection
        icon={<VideocamIcon aria-hidden />}
        title={t('videoVisit.devices.camera')}
        unnamedKey="videoVisit.devices.unnamedCamera"
        devices={listOf('videoinput')}
        activeId={activeIdOf('videoinput', cameraTrack)}
        onSelect={switchTo('videoinput')}
      />
      <DeviceSection
        icon={<MicIcon aria-hidden />}
        title={t('videoVisit.devices.microphone')}
        unnamedKey="videoVisit.devices.unnamedMicrophone"
        devices={listOf('audioinput')}
        activeId={activeIdOf('audioinput', microphoneTrack)}
        onSelect={switchTo('audioinput')}
      />
      {supportsAudioOutputSelection() && (
        <DeviceSection
          icon={<VolumeUpIcon aria-hidden />}
          title={t('videoVisit.devices.speaker')}
          unnamedKey="videoVisit.devices.unnamedSpeaker"
          devices={listOf('audiooutput')}
          activeId={activeIdOf('audiooutput')}
          onSelect={switchTo('audiooutput')}
        />
      )}
    </Menu>
  );
};

export default DeviceMenu;
